import { useMemo } from 'react';
import {
  ArrowRight,
  ExternalLink,
  Flame,
  Lightbulb,
  Target,
  Activity,
  Users,
  Sparkles,
  Palette,
  Sparkle,
  Tv,
  CalendarClock,
  Compass,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ImageWithFallback } from '@/components/figma/ImageWithFallback';
import { formatVelocity, type Trend, type TrendAngle, type TrendAngleType, type TrendSignal } from '@/utils/trend';

// Map angle types to an icon + label pair. Tuned so the drawer's strategic
// playbook reads like distinct lenses rather than a wall of bullets.
const ANGLE_META: Record<TrendAngleType, { label: string; Icon: typeof Users }> = {
  audience: { label: 'Audience', Icon: Users },
  content_concept: { label: 'Content concept', Icon: Palette },
  creator_strategy: { label: 'Creator strategy', Icon: Sparkle },
  brand_strategy: { label: 'Brand strategy', Icon: Compass },
  timing: { label: 'Timing & runway', Icon: CalendarClock },
  channel_mix: { label: 'Channel mix', Icon: Tv },
};

const SIGNAL_LABELS: Record<TrendSignal['source'], string> = {
  beatly_mentions: 'Beatly mentions',
  google_trends: 'Google Trends · related',
  google_trends_now: 'Google Trends · now',
  google_news: 'Google News',
  tiktok_cc: 'TikTok Creative Center',
  meta_ad_library: 'Meta Ad Library',
  reddit: 'Reddit',
  ad_anomaly: 'Ad anomaly',
  youtube: 'YouTube',
  newsapi: 'NewsAPI',
};

export interface TrendDetailDrawerProps {
  trend: Trend | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply?: (trend: Trend) => void;
}

export function TrendDetailDrawer({ trend, open, onOpenChange, onApply }: TrendDetailDrawerProps) {
  const chartData = useMemo(() => {
    if (!trend) return [];
    return trend.snapshots.map((s) => ({
      day: s.snapshot_date.slice(5),
      score: s.virality_score,
      mentions: s.mention_count,
    }));
  }, [trend]);

  if (!trend) return null;

  const signalsBySource = new Map<TrendSignal['source'], TrendSignal[]>();
  for (const s of trend.signals) {
    const arr = signalsBySource.get(s.source) ?? [];
    arr.push(s);
    signalsBySource.set(s.source, arr);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto p-0 sm:max-w-[640px]">
        {trend.metadata.cover_source === 'tiktok' && trend.metadata.cover_embed_url ? (
          // Live TikTok embed — vertical 9:16 player centered in a dark backdrop.
          // Capped height so the rest of the drawer still fits on one scroll.
          <div className="relative w-full bg-black px-4 pb-4 pt-5">
            <div className="mx-auto aspect-[9/16] max-h-[520px] w-full max-w-[320px] overflow-hidden rounded-xl bg-black">
              <iframe
                src={trend.metadata.cover_embed_url}
                title={`TikTok video — ${trend.title}`}
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                allowFullScreen
                className="h-full w-full border-0"
              />
            </div>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
              <Badge className="border-0 bg-white/90 text-[10px] font-bold uppercase tracking-wider text-gray-900">
                {trend.category}
              </Badge>
              <Badge className="border-0 bg-white/90 text-[10px] font-bold uppercase tracking-wider text-gray-900">
                {trend.status}
              </Badge>
              <Badge className="border-0 bg-white/90 text-[10px] font-bold uppercase tracking-wider text-gray-900">
                {trend.geo_scope}
              </Badge>
              {trend.metadata.cover_video_url && (
                <a
                  href={trend.metadata.cover_video_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 rounded-md bg-white/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-gray-900 hover:bg-white"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="h-3 w-3" />
                  Open on TikTok
                </a>
              )}
            </div>
          </div>
        ) : (
          // Magazine-style hero for google_images and stock covers: taller image,
          // gradient scrim for legibility, optional source caption below.
          <div className="relative w-full overflow-hidden bg-gray-100">
            <div className="relative h-[360px] w-full">
              <ImageWithFallback
                src={trend.metadata.cover_image_url ?? ''}
                alt={trend.title}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-4 left-5 right-5 flex flex-wrap items-center gap-2">
                <Badge className="border-0 bg-white/90 text-[10px] font-bold uppercase tracking-wider text-gray-900">
                  {trend.category}
                </Badge>
                <Badge className="border-0 bg-white/90 text-[10px] font-bold uppercase tracking-wider text-gray-900">
                  {trend.status}
                </Badge>
                <Badge className="border-0 bg-white/90 text-[10px] font-bold uppercase tracking-wider text-gray-900">
                  {trend.geo_scope}
                </Badge>
              </div>
            </div>
            {trend.metadata.cover_source === 'google_images' && (
              <p className="px-5 py-2 text-[11px] italic text-gray-500">
                Image: Google Images · representative cover, not a specific cited source.
              </p>
            )}
          </div>
        )}

        <div className="flex flex-col gap-5 p-5">
          <SheetHeader className="p-0 text-left">
            <SheetTitle className="text-[22px]">{trend.title}</SheetTitle>
            <SheetDescription className="text-[13px]">{trend.description}</SheetDescription>
          </SheetHeader>

          <div className="grid grid-cols-3 gap-3">
            <ScoreTile label="Virality" value={trend.virality_score} icon={<Flame className="h-4 w-4 text-orange-500" />} />
            <ScoreTile
              label="Relevance"
              value={trend.relevance.relevance_score}
              icon={<Target className="h-4 w-4 text-[#be43ff]" />}
            />
            <ScoreTile
              label="Signals"
              value={trend.signals.length}
              icon={<Activity className="h-4 w-4 text-sky-500" />}
              raw
            />
          </div>

          <div className="rounded-2xl border border-[#e5d4ff] bg-[#f3ecff] p-4">
            <div className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-[#be43ff]" />
              <span className="text-[11px] font-bold uppercase tracking-tight text-[#2e024c]">
                Why this matters for you
              </span>
            </div>
            <p className="mt-2 text-[13px] leading-snug text-[#2e024c]/90">
              {trend.relevance.reasoning}
            </p>
            {/* Angles take precedence when present — they're the real tactical plan. */}
            {(!trend.metadata.angles || trend.metadata.angles.length === 0) && (
              <p className="mt-3 text-[13px] italic leading-snug text-[#2e024c]">
                "{trend.relevance.suggested_action}"
              </p>
            )}
            <Button
              type="button"
              onClick={() => onApply?.(trend)}
              className="mt-3 h-9 gap-2 rounded-xl bg-[#2e024c] px-4 text-xs text-white shadow-md shadow-purple-200 hover:bg-[#3a0d5e]"
            >
              Create campaign from this trend
              <ArrowRight className="h-3 w-3" />
            </Button>
          </div>

          {trend.metadata.angles && trend.metadata.angles.length > 0 && (
            <div className="rounded-2xl border border-gray-100 bg-white p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-[13px] font-semibold text-[#101828]">
                    Strategic playbook
                  </h4>
                  <p className="text-[11px] text-gray-500">
                    Influencer campaigns take 3–6 weeks — these are different lenses for the next planned push,
                    not instant activations.
                  </p>
                </div>
              </div>
              <div className="mt-3 flex flex-col gap-2">
                {trend.metadata.angles.map((angle, i) => (
                  <AngleCard key={`${angle.type}-${i}`} angle={angle} />
                ))}
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-gray-100 bg-white p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-[13px] font-semibold text-[#101828]">Virality trajectory</h4>
                <p className="text-[11px] text-gray-500">21-day snapshot from internal + external signals.</p>
              </div>
              <span className="text-[11px] text-gray-500">
                First seen {new Date(trend.first_seen_at).toLocaleDateString()}
              </span>
            </div>
            <div className="mt-3 h-[160px] w-full">
              <ResponsiveContainer>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id={`detail-${trend.id}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#be43ff" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#be43ff" stopOpacity={0.04} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAECF0" />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} width={28} />
                  <Tooltip formatter={(v: number) => v.toFixed(0)} />
                  <Area type="monotone" dataKey="score" stroke="#be43ff" strokeWidth={2} fill={`url(#detail-${trend.id})`} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white p-4">
            <h4 className="text-[13px] font-semibold text-[#101828]">Signals detected</h4>
            <p className="text-[11px] text-gray-500">
              A trend exists when ≥3 signals from ≥2 sources agree. This trend has {trend.signals.length} across{' '}
              {signalsBySource.size} source{signalsBySource.size === 1 ? '' : 's'}.
            </p>
            <div className="mt-3 flex flex-col gap-2">
              {Array.from(signalsBySource.entries()).map(([source, list]) => (
                <div key={source} className="rounded-xl border border-gray-100 p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[12px] font-semibold text-[#344054]">
                      {SIGNAL_LABELS[source]}
                    </span>
                    <span className="text-[11px] text-gray-500">
                      {list.length} hit{list.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <ul className="mt-2 flex flex-col gap-1 text-[11px] text-gray-600">
                    {list.map((s) => {
                      // Dedupe: when keyword and hashtag are the same string (common for
                      // TikTok, where the fetcher writes both to the same hashtag_name),
                      // show one label, not two. For TikTok render with a `#` prefix so
                      // the hashtag reads as such at a glance.
                      const kw = typeof s.raw_data.keyword === 'string' ? s.raw_data.keyword.trim() : '';
                      const ht = typeof s.raw_data.hashtag === 'string' ? s.raw_data.hashtag.trim() : '';
                      const same = !!kw && !!ht && kw.toLowerCase() === ht.toLowerCase();
                      const label = source === 'tiktok_cc'
                        ? (ht || kw ? `#${(ht || kw).replace(/^#+/, '')}` : '')
                        : same ? ht : [kw, ht].filter(Boolean).join(' · ');
                      return (
                      <li key={s.id} className="flex flex-wrap items-center gap-2">
                        {source !== 'tiktok_cc' && (
                          <span className="font-medium text-gray-700">{s.signal_type.replace(/_/g, ' ')}</span>
                        )}
                        {label && (
                          <span className={source === 'tiktok_cc' ? 'font-semibold text-[#2e024c]' : ''}>
                            {source !== 'tiktok_cc' && '· '}{label}
                          </span>
                        )}
                        {s.raw_data.velocity_ratio != null && (
                          <Badge className="border-0 bg-orange-100 text-[10px] text-orange-700">
                            vel {formatVelocity(s.raw_data.velocity_ratio)}
                          </Badge>
                        )}
                        {s.raw_data.z_score != null && (
                          <Badge className="border-0 bg-sky-100 text-[10px] text-sky-700">
                            z {(s.raw_data.z_score as number).toFixed(1)}
                          </Badge>
                        )}
                        {source === 'tiktok_cc' && s.raw_data.rank_position != null && (
                          <Badge className="border-0 bg-fuchsia-100 text-[10px] text-fuchsia-700">
                            #{s.raw_data.rank_position as number} in {s.geo}
                          </Badge>
                        )}
                        {source === 'tiktok_cc' && typeof s.raw_data.rank_change === 'number' && s.raw_data.rank_change !== 0 && (
                          <Badge className={`border-0 text-[10px] ${(s.raw_data.rank_change as number) > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                            {(s.raw_data.rank_change as number) > 0 ? '▲' : '▼'} {Math.abs(s.raw_data.rank_change as number)}
                          </Badge>
                        )}
                        {source === 'tiktok_cc' && typeof s.raw_data.posts === 'number' && (
                          <Badge className="border-0 bg-gray-100 text-[10px] text-gray-700">
                            {(s.raw_data.posts as number).toLocaleString()} posts
                          </Badge>
                        )}
                        {s.raw_data.sample_post_url && (
                          <a
                            href={String(s.raw_data.sample_post_url)}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="inline-flex items-center gap-1 text-[#be43ff]"
                          >
                            <ExternalLink className="h-3 w-3" />
                            sample
                          </a>
                        )}
                      </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white p-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-gray-500" />
              <h4 className="text-[13px] font-semibold text-[#101828]">Related</h4>
            </div>
            {trend.metadata.hashtags && trend.metadata.hashtags.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {trend.metadata.hashtags.map((h) => (
                  <Badge key={h} className="border-0 bg-gray-100 text-[11px] text-gray-700">
                    <Sparkles className="mr-1 h-3 w-3 text-[#be43ff]" />
                    {h}
                  </Badge>
                ))}
              </div>
            )}
            {trend.metadata.related_keywords && trend.metadata.related_keywords.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {trend.metadata.related_keywords.map((k) => (
                  <Badge key={k} className="border-0 bg-gray-50 text-[11px] text-gray-600">
                    {k}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ScoreTile({
  label,
  value,
  icon,
  raw = false,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  raw?: boolean;
}) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-3 text-center">
      <div className="flex items-center justify-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-[20px] font-semibold text-[#101828]">
        {raw ? value : `${value}`}
      </div>
    </div>
  );
}

function AngleCard({ angle }: { angle: TrendAngle }) {
  const meta = ANGLE_META[angle.type] ?? ANGLE_META.audience;
  const Icon = meta.Icon;
  return (
    <div className="flex gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3">
      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
        <Icon className="h-3.5 w-3.5 text-[#be43ff]" />
      </div>
      <div className="flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#be43ff]">
            {meta.label}
          </span>
          <span className="text-[13px] font-semibold text-[#101828]">{angle.title}</span>
        </div>
        <p className="mt-1 text-[12px] leading-relaxed text-gray-600">{angle.detail}</p>
      </div>
    </div>
  );
}
